import type { DrawNumber } from '../../../../schema/draw/entry.js';
import type { Definition } from '../../../../schema/frameworks/entry.js';
import type { marketplaceBalanceApiResponse } from '../../../../schema/marketplace/entry.js';
import {
  AuthenticatedError,
  type IAuthorizedUseCase,
  type IGetMarketplaceBalanceUseCase,
} from '../../../domain/entry.js';

export type IGetMarketplaceBalanceController = ReturnType<typeof getMarketplaceBalanceController>;
type MarketplaceBalanceResponse = Extract<
  ReturnType<(typeof marketplaceBalanceApiResponse)['parse']>,
  { data: object }
>['data'];

type GetMarketplaceBalanceControllerParams = {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
};

/**
 * Creates a controller for retrieving marketplace balance.
 *
 * @param getMarketplaceBalanceUseCase - The use case for getting marketplace balance
 * @param authorizedUseCase - The use case for checking user roles
 * @returns A controller function that executes the marketplace balance use case
 */
export function getMarketplaceBalanceController(
  getMarketplaceBalanceUseCase: IGetMarketplaceBalanceUseCase,
  authorizedUseCase?: IAuthorizedUseCase
) {
  return async function controller({
    product,
    drawNumber,
  }: GetMarketplaceBalanceControllerParams): Promise<MarketplaceBalanceResponse> {
    let isUserRole = true;
    // Only able to check user role server side.
    if (authorizedUseCase) {
      isUserRole = await authorizedUseCase.execute({ role: 'Player' });
    }
    if (!isUserRole) {
      throw new AuthenticatedError('User not signed in');
    }
    return await getMarketplaceBalanceUseCase.execute({ product, drawNumber });
  };
}
